import { createContext, useContext, useCallback, useRef, useState, useEffect, type ReactNode } from "react";

export interface NowPlayingInfo {
  id: string;
  title: string;
  audioUrl: string;
  durationMs: number;
  slug: string;
}

interface PlayingSound {
  id: string;
  audio: HTMLAudioElement;
  info: NowPlayingInfo;
}

interface ComboQueueItem {
  id: string;
  audioUrl: string;
  title: string;
  durationMs: number;
  slug: string;
}

interface AudioContextValue {
  play: (soundId: string, audioUrl: string, info?: Omit<NowPlayingInfo, "id" | "audioUrl">) => void;
  stop: (soundId: string) => void;
  stopAll: () => void;
  isPlaying: (soundId: string) => boolean;
  playingSounds: Set<string>;
  nowPlaying: NowPlayingInfo | null;
  nowPlayingProgress: number;
  polyphonic: boolean;
  setPolyphonic: (v: boolean) => void;
  playbackRate: number;
  setPlaybackRate: (v: number) => void;
  playCombo: (comboId: string, comboName: string, sounds: ComboQueueItem[]) => void;
  activeComboId: string | null;
  comboProgress: { current: number; total: number } | null;
}

const AudioCtx = createContext<AudioContextValue | null>(null);

export function AudioProvider({ children }: { children: ReactNode }) {
  const activeSounds = useRef<Map<string, PlayingSound>>(new Map());
  const [playingSounds, setPlayingSounds] = useState<Set<string>>(new Set());
  const [nowPlaying, setNowPlaying] = useState<NowPlayingInfo | null>(null);
  const [nowPlayingProgress, setNowPlayingProgress] = useState(0);
  const progressRef = useRef<number>(0);
  const [polyphonic, setPolyphonic] = useState(() => {
    return localStorage.getItem("wizard-sounds-polyphonic") !== "false";
  });
  const [playbackRate, setPlaybackRate] = useState(() => {
    const saved = Number(localStorage.getItem("wizard-sounds-rate"));
    return saved >= 0.5 && saved <= 2 ? saved : 1;
  });
  const [activeComboId, setActiveComboId] = useState<string | null>(null);
  const [comboProgress, setComboProgress] = useState<{ current: number; total: number } | null>(null);
  const comboQueueRef = useRef<{
    comboId: string;
    comboName: string;
    sounds: ComboQueueItem[];
    preloaded: HTMLAudioElement[];
    currentIndex: number;
  } | null>(null);

  const updatePlayingSet = useCallback(() => {
    const keys = new Set(activeSounds.current.keys());
    setPlayingSounds(keys);
    if (keys.size === 0) {
      setNowPlaying(null);
      setNowPlayingProgress(0);
    }
  }, []);

  useEffect(() => {
    let raf: number;
    const tick = () => {
      if (nowPlaying) {
        const playing = activeSounds.current.get(nowPlaying.id);
        if (playing && playing.audio.duration) {
          const progress = playing.audio.currentTime / playing.audio.duration;
          if (Math.abs(progress - progressRef.current) > 0.005) {
            progressRef.current = progress;
            setNowPlayingProgress(progress);
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [nowPlaying]);

  const stop = useCallback(
    (soundId: string) => {
      const playing = activeSounds.current.get(soundId);
      if (playing) {
        playing.audio.pause();
        playing.audio.currentTime = 0;
        activeSounds.current.delete(soundId);
        updatePlayingSet();
      }
    },
    [updatePlayingSet],
  );

  const stopAll = useCallback(() => {
    activeSounds.current.forEach((playing) => {
      playing.audio.pause();
      playing.audio.currentTime = 0;
    });
    activeSounds.current.clear();
    // Clean up preloaded audio elements
    const queue = comboQueueRef.current;
    if (queue) {
      queue.preloaded.forEach((a) => { a.pause(); a.src = ""; });
    }
    comboQueueRef.current = null;
    setActiveComboId(null);
    setComboProgress(null);
    updatePlayingSet();
  }, [updatePlayingSet]);

  const play = useCallback(
    (soundId: string, audioUrl: string, info?: Omit<NowPlayingInfo, "id" | "audioUrl">) => {
      if (activeSounds.current.has(soundId)) {
        stop(soundId);
        return;
      }

      if (!polyphonic) {
        stopAll();
      }

      const audio = new Audio(audioUrl);
      audio.volume = 1;
      audio.playbackRate = playbackRate;

      const soundInfo: NowPlayingInfo = {
        id: soundId,
        audioUrl,
        title: info?.title ?? "Playing...",
        durationMs: info?.durationMs ?? 0,
        slug: info?.slug ?? "",
      };

      audio.addEventListener("ended", () => {
        activeSounds.current.delete(soundId);
        // Advance combo queue if this was part of a combo
        const queue = comboQueueRef.current;
        if (queue && queue.currentIndex < queue.sounds.length - 1) {
          queue.currentIndex++;
          setComboProgress({ current: queue.currentIndex + 1, total: queue.sounds.length });
          advanceCombo();
        } else if (queue) {
          comboQueueRef.current = null;
          setActiveComboId(null);
          setComboProgress(null);
        }
        updatePlayingSet();
      });

      audio.addEventListener("error", () => {
        activeSounds.current.delete(soundId);
        comboQueueRef.current = null;
        setActiveComboId(null);
        setComboProgress(null);
        updatePlayingSet();
      });

      activeSounds.current.set(soundId, { id: soundId, audio, info: soundInfo });
      setNowPlaying(soundInfo);
      setNowPlayingProgress(0);
      progressRef.current = 0;
      updatePlayingSet();
      audio.play();

      fetch(`/api/sounds/${soundId}/play`, { method: "POST" }).catch(() => {});
    },
    [polyphonic, playbackRate, stop, stopAll, updatePlayingSet],
  );

  const advanceCombo = useCallback(() => {
    const queue = comboQueueRef.current;
    if (!queue) return;

    const idx = queue.currentIndex;
    const item = queue.sounds[idx];
    const audio = queue.preloaded[idx];
    if (!audio || !item) return;

    // Ensure current playback rate is applied
    audio.playbackRate = playbackRate;

    // --- Gapless transition: start playback BEFORE React state updates ---
    let raf: number;
    const startAndPoll = () => {
      audio.play().catch(() => {});
      raf = requestAnimationFrame(pollEnd);
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
      startAndPoll();
    } else {
      audio.addEventListener("canplaythrough", startAndPoll, { once: true });
      audio.load();
    }

    // Guard against double-advance from both poll and ended event
    let advanced = false;
    const tryAdvanceNext = () => {
      if (advanced) return;
      advanced = true;
      const q = comboQueueRef.current;
      if (q && q.currentIndex < q.sounds.length - 1) {
        q.currentIndex++;
        setComboProgress({ current: q.currentIndex + 1, total: q.sounds.length });
        advanceCombo();
      } else if (q) {
        comboQueueRef.current = null;
        setActiveComboId(null);
        setComboProgress(null);
      }
    };

    // Poll near-end (~80ms before) to pre-start next sound for gapless playback
    function pollEnd() {
      if (audio.paused) return; // stopped externally
      if (audio.duration > 0 && audio.duration - audio.currentTime < 0.08) {
        tryAdvanceNext();
        return;
      }
      raf = requestAnimationFrame(pollEnd);
    }

    audio.addEventListener("ended", () => {
      cancelAnimationFrame(raf);
      tryAdvanceNext(); // no-op if already advanced via poll
      activeSounds.current.delete(item.id);
      updatePlayingSet();
    });

    audio.addEventListener("error", () => {
      cancelAnimationFrame(raf);
      activeSounds.current.delete(item.id);
      comboQueueRef.current = null;
      setActiveComboId(null);
      setComboProgress(null);
      updatePlayingSet();
    });

    // UI state updates (after playback has already started)
    const soundInfo: NowPlayingInfo = {
      id: item.id,
      audioUrl: item.audioUrl,
      title: item.title,
      durationMs: item.durationMs,
      slug: item.slug,
    };

    activeSounds.current.set(item.id, { id: item.id, audio, info: soundInfo });
    setNowPlaying({
      ...soundInfo,
      title: `${queue.comboName} (${idx + 1}/${queue.sounds.length})`,
    });
    setNowPlayingProgress(0);
    progressRef.current = 0;
    updatePlayingSet();
  }, [playbackRate, updatePlayingSet]);

  const playCombo = useCallback(
    (comboId: string, comboName: string, sounds: ComboQueueItem[]) => {
      if (sounds.length === 0) return;

      // If already playing this combo, stop it
      if (activeComboId === comboId) {
        stopAll();
        return;
      }

      stopAll();

      // Preload all audio elements so transitions are instant
      const preloaded = sounds.map((s) => {
        const a = new Audio(s.audioUrl);
        a.volume = 1;
        a.playbackRate = playbackRate;
        a.preload = "auto";
        a.load();
        return a;
      });

      comboQueueRef.current = { comboId, comboName, sounds, preloaded, currentIndex: 0 };
      setActiveComboId(comboId);
      setComboProgress({ current: 1, total: sounds.length });

      // Start first sound using preloaded element
      advanceCombo();

      // Record combo play
      fetch(`/api/combos/${comboId}/play`, { method: "POST" }).catch(() => {});
    },
    [activeComboId, stopAll, playbackRate, advanceCombo],
  );

  const isPlaying = useCallback(
    (soundId: string) => playingSounds.has(soundId),
    [playingSounds],
  );

  const handleSetPolyphonic = useCallback((v: boolean) => {
    setPolyphonic(v);
    localStorage.setItem("wizard-sounds-polyphonic", String(v));
  }, []);

  const handleSetPlaybackRate = useCallback((v: number) => {
    const clamped = Math.min(2, Math.max(0.5, Math.round(v * 100) / 100));
    setPlaybackRate(clamped);
    localStorage.setItem("wizard-sounds-rate", String(clamped));
    // Apply live to anything currently playing so the speed change is instant.
    activeSounds.current.forEach((p) => {
      p.audio.playbackRate = clamped;
    });
  }, []);

  return (
    <AudioCtx.Provider
      value={{
        play,
        stop,
        stopAll,
        isPlaying,
        playingSounds,
        nowPlaying,
        nowPlayingProgress,
        polyphonic,
        setPolyphonic: handleSetPolyphonic,
        playbackRate,
        setPlaybackRate: handleSetPlaybackRate,
        playCombo,
        activeComboId,
        comboProgress,
      }}
    >
      {children}
    </AudioCtx.Provider>
  );
}

export function useAudio() {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error("useAudio must be used within AudioProvider");
  return ctx;
}
